import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { supabase } from '@/lib/supabase';

/**
 * Hook to load leave history page data with server-side pagination and filtering
 * Replaces 2 sequential queries + client-side filtering with single optimized RPC
 *
 * @param {string} userId - User profile ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {number} options.debounceMs - Search debounce delay (default 300ms)
 * @returns {Object} - { requests, balance, stats, loading, error, pagination, filters, refetch, withdrawRequest }
 */
export function useLeaveHistory(userId, options = {}) {
  const { pageSize: initialPageSize = 25, debounceMs = 300 } = options;

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() => getStoredPageSize(initialPageSize));
  const setPageSize = useCallback((val) => { _setPageSize(val); setStoredPageSize(val); }, []);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for request management
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter]);

  /**
   * Load leave history data from server
   */
  const loadData = useCallback(async () => {
    if (!userId) {
      console.log('[useLeaveHistory] Missing userId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useLeaveHistory] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useLeaveHistory] Loading data with single RPC call...');
      console.time('[useLeaveHistory] RPC call duration');

      const currentYear = new Date().getFullYear();
      const offset = (page - 1) * pageSize;

      // Build filter params
      const statusParam = statusFilter !== 'all' ? statusFilter : null;
      const typeParam = typeFilter !== 'all' ? typeFilter : null;
      const searchParam = debouncedSearch || null;

      // Single RPC call replaces 2 queries + client-side filtering!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_leave_history_page',
        {
          p_user_id: userId,
          p_year: currentYear,
          p_status: statusParam,
          p_leave_type: typeParam,
          p_search: searchParam,
          p_limit: pageSize,
          p_offset: offset
        }
      );

      console.timeEnd('[useLeaveHistory] RPC call duration');

      if (rpcError) {
        console.error('[useLeaveHistory] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useLeaveHistory] RPC response:', {
        requestsCount: rpcData?.requests?.length || 0,
        totalCount: rpcData?.total_count || 0,
        filteredCount: rpcData?.filtered_count || 0,
        hasBalance: !!rpcData?.balance
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useLeaveHistory] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [userId, page, pageSize, statusFilter, typeFilter, debouncedSearch]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Withdraw a leave request with optimistic update
   * @param {Object} request - The request to withdraw
   * @returns {Promise<boolean>} - Success status
   */
  const withdrawRequest = useCallback(async (request) => {
    if (!request?.id || !userId) {
      return false;
    }

    // Optimistic update
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        requests: prev.requests.map(r =>
          r.id === request.id ? { ...r, status: 'withdrawn' } : r
        ),
        stats: {
          ...prev.stats,
          pending: prev.stats.pending - (request.status === 'pending' ? 1 : 0),
          approved: prev.stats.approved - (request.status === 'approved' ? 1 : 0),
          withdrawn: prev.stats.withdrawn + 1
        }
      };
    });

    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({ status: 'withdrawn' })
        .eq('id', request.id)
        .eq('user_id', userId);

      if (error) {
        // Revert optimistic update on error
        loadData();
        throw error;
      }

      return true;
    } catch (err) {
      console.error('[useLeaveHistory] Withdraw error:', err);
      return false;
    }
  }, [userId, loadData]);

  /**
   * Change page size and reset to first page
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  // Computed pagination values
  const totalPages = useMemo(() => {
    const filteredCount = data?.filtered_count || 0;
    return Math.ceil(filteredCount / pageSize);
  }, [data?.filtered_count, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    requests: data?.requests || [],
    balance: data?.balance || null,
    stats: data?.stats || { total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 },

    // Loading state
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: data?.total_count || 0,
      filteredItems: data?.filtered_count || 0,
      setPage,
      setPageSize: handlePageSizeChange
    },

    // Filters
    filters: {
      status: statusFilter,
      type: typeFilter,
      search: searchTerm,
      setStatus: setStatusFilter,
      setType: setTypeFilter,
      setSearch: setSearchTerm
    },

    // Actions
    refetch: loadData,
    withdrawRequest
  };
}
